import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import SideBarModal from '../modal/SideBarModal';
import { FooterAction } from '../../interfaces';
import useGetCustomerOverview from '../../hooks/customers/useGetCustomerOverview';
import { createModalData } from '../../utils/modal/customerModal';
import { createFooterAction } from '../../helpers/global';
import {
  CustomerFileForm,
  customerFileSchema,
  CustomerGuest,
  CustomerOverview,
} from '../../types/customers';
import FormSmallModal, { FormData } from '../global/FormSmallModal';
import { ACCEPTED_FILE_TYPES } from '../../constants/customers';
import useUploadCustomerFile from '../../hooks/customers/useUploadCustomerFile';
import useDeleteCustomerFile from '../../hooks/customers/useDeleteCustomerFile';
import useBlockCustomer from '../../hooks/customers/useBlockCustomer';
import useConfirm from '../../hooks/useConfirm';
import FullPageModal from '../modal/FullPageModal';
import CustomerForm from '../form/CustomerForm';
import GuestForm from '../form/GuestForm';
import useLoadingModal from '../../hooks/global/useLoadingModal';
import useUser from '../../hooks/global/useUser';
import { openCustomerChat } from '../../helpers/customers';
import useGiftCardActions from '../../hooks/giftCards/useGiftCardActions';
import GiftCardDetailsModal from '../giftCards/GiftCardDetailsModal';
import { isUserAdminOrManager } from '../../utils/global/helper';
import { CustomerBlockScope } from '../../services/customers';

interface Props {
  customerId: string | null;
  isOpen: boolean;
  onClose: () => void;
  loadingTitle?: string;
  onCustomerSave?: () => void | Promise<void>;
  onSelectAppointment?: () => void;
}

const constructCustomerName = (customer: CustomerOverview) =>
  `${customer.user.firstName || ''} ${customer.user.lastName || ''}`.trim();

function CustomerSideBarModal({
  customerId,
  isOpen,
  onClose,
  loadingTitle,
  onCustomerSave,
  onSelectAppointment,
}: Props) {
  const open = !!customerId && isOpen;
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false);
  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  const [editingGuest, setEditingGuest] = useState<CustomerGuest | null>(null);
  const [detailsGiftCardId, setDetailsGiftCardId] = useState<string | null>(
    null,
  );

  const { user } = useUser();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();
  const { getOptions: getGiftCardOptions } = useGiftCardActions();
  const {
    data: customer,
    isLoading,
    isError,
    isPlaceholderData,
    refetch,
  } = useGetCustomerOverview(customerId, {
    enabled: open,
  });

  const onError = useCallback((type: 'upload' | 'delete') => {
    toast.error(`Failed to ${type} file`);
  }, []);

  const { mutateAsync: uploadCustomerFile, isPending: isUploading } =
    useUploadCustomerFile({
      refetch,
      onSuccess: () => setIsFileUploadModalOpen(false),
      onError: () => onError('upload'),
    });

  const {
    mutate: deleteCustomerFile,
    isPending: isDeleting,
    variables: deleteVariables,
  } = useDeleteCustomerFile({
    refetch,
    onError: () => onError('delete'),
  });
  const fileIdBeingDeleted = isDeleting ? deleteVariables?.fileId : undefined;

  const { mutateAsync: toggleCustomerBlock, isPending: isTogglingBlock } =
    useBlockCustomer({
      onSuccess: () => {
        refetch();
      },
      onError: () => {
        toast.error('Failed to update customer block status');
      },
    });

  const handleDeleteFile = useCallback(
    async (fileId: string) => {
      if (!customerId) return;

      const confirmed = await confirm({
        title: 'Delete file',
        message:
          'Are you sure you want to delete this file? This action cannot be undone.',
      });
      if (!confirmed) return;

      deleteCustomerFile({
        customerId,
        fileId,
      });
    },
    [customerId, deleteCustomerFile, confirm],
  );

  const customerName =
    customer && !isError
      ? constructCustomerName(customer)
      : loadingTitle || '-';

  const modalData = useMemo(
    () =>
      customer
        ? createModalData(customer, {
            deleteFile: handleDeleteFile,
            fileIdBeingDeleted,
            onSelectAppointment,
            onEditGuest: setEditingGuest,
            onSelectGiftCard: setDetailsGiftCardId,
          })
        : null,
    [customer, fileIdBeingDeleted, handleDeleteFile, onSelectAppointment],
  );

  const onOpenCustomerChat = useCallback(async () => {
    if (!customer || !user?.business) return;
    await openCustomerChat({
      openLoadingModal,
      closeLoadingModal,
      navigate,
      customerId: customer.customerId,
      user,
    });
  }, [closeLoadingModal, customer, navigate, openLoadingModal, user]);

  const isManager = !!user?.role && isUserAdminOrManager(user.role);
  const canShopBlock = isManager && user?.business?.type === 'STUDIO';
  const blockScope: CustomerBlockScope = canShopBlock ? 'SHOP' : 'ARTIST';
  const isScopedBlocked = canShopBlock
    ? !!customer?.shopBlocked
    : !!customer?.artistBlocked;

  const handleToggleBlock = useCallback(async () => {
    if (!customer || !user?.business?.id || isTogglingBlock) return;

    const willBlock = !isScopedBlocked;
    const scopeLabel = canShopBlock ? 'for the shop' : 'for you';
    const confirmed = await confirm({
      title: willBlock
        ? `Block customer ${scopeLabel}`
        : `Unblock customer ${scopeLabel}`,
      message: willBlock
        ? canShopBlock
          ? `Are you sure you want to block ${customerName} for the entire shop? They will not be able to book or send chat messages to this business.`
          : `Are you sure you want to block ${customerName} for you? They will not be able to book or send chat messages to you.`
        : `Are you sure you want to unblock ${customerName} ${scopeLabel}?`,
      positiveText: willBlock ? 'Block' : 'Unblock',
    });
    if (!confirmed) return;

    try {
      openLoadingModal({
        header: willBlock ? 'Blocking customer...' : 'Unblocking customer...',
        message: 'Please wait',
      });
      await toggleCustomerBlock({
        customerId: customer.customerId,
        businessId: user.business.id,
        scope: blockScope,
        block: willBlock,
      });
      toast.success(
        willBlock
          ? canShopBlock
            ? 'Customer has been blocked for the shop'
            : 'Customer has been blocked for you'
          : canShopBlock
          ? 'Customer has been unblocked for the shop'
          : 'Customer has been unblocked for you',
      );
    } catch {
      // Error toast handled by useBlockCustomer onError
    } finally {
      closeLoadingModal();
    }
  }, [
    blockScope,
    canShopBlock,
    closeLoadingModal,
    confirm,
    customer,
    customerName,
    isScopedBlocked,
    isTogglingBlock,
    openLoadingModal,
    toggleCustomerBlock,
    user?.business?.id,
  ]);

  const footerActions = useMemo(
    () =>
      [
        !!customer &&
          createFooterAction('Edit Customer', () => {
            if (!customer) return;
            setIsEditCustomerModalOpen(true);
          }),
      ].filter(Boolean) as FooterAction[],
    [customer],
  );

  const moreActions = useMemo(
    () =>
      customer
        ? [
            {
              label: 'Upload file for customer',
              action: () => setIsFileUploadModalOpen(true),
            },
            {
              label: 'Open Chat',
              action: onOpenCustomerChat,
            },
            {
              label: isScopedBlocked
                ? canShopBlock
                  ? 'Unblock for shop'
                  : 'Unblock for me'
                : canShopBlock
                ? 'Block for shop'
                : 'Block for me',
              action: handleToggleBlock,
            },
          ]
        : [],
    [
      canShopBlock,
      customer,
      handleToggleBlock,
      isScopedBlocked,
      onOpenCustomerChat,
    ],
  );

  const fileUploadForm: FormData<CustomerFileForm> = {
    initialValues: {
      file: null as any,
    },
    inputRows: [
      [
        {
          inputType: 'file',
          label: 'Upload file',
          name: 'file',
          accept: ACCEPTED_FILE_TYPES,
          acceptLabel: 'Image, PDF',
          acceptLabelNewLine: true,
          acceptLabelSx: {
            textAlign: 'center',
          },
          disabled: isUploading,
        },
      ],
    ],
    onSubmit: async values => {
      if (!customerId || !values.file) return;
      await uploadCustomerFile({
        customerId,
        file: values.file,
      });
    },
    submitButtonText: 'Upload',
    title: 'Upload file for customer',
    validationSchema: customerFileSchema,
  };

  const selectedGiftCard = customer?.giftCards?.find(
    giftCard => giftCard.id === detailsGiftCardId,
  );
  const giftCardActionTarget =
    selectedGiftCard && customer
      ? {
          id: selectedGiftCard.id,
          currentBalance: selectedGiftCard.currentBalance,
          customerId: customer.customerId,
          customer: { user: customer.user },
        }
      : null;

  return (
    <>
      <SideBarModal
        isLoading={isLoading || isPlaceholderData}
        modalOpen={open}
        setModalOpen={onClose}
        name={customerName}
        sections={
          isError
            ? [
                {
                  name: '',
                  rows: [
                    {
                      label: '',
                      type: 'TEXT',
                      value: ['There was an error loading this customer.'],
                    },
                  ],
                  values: [],
                },
              ]
            : modalData?.sections ?? []
        }
        moreActions={moreActions}
        footerActions={footerActions}
      />
      {isFileUploadModalOpen && (
        <FormSmallModal
          maxWidth="480px"
          form={fileUploadForm}
          open
          onClose={() => setIsFileUploadModalOpen(false)}
        />
      )}

      <GiftCardDetailsModal
        open={!!detailsGiftCardId}
        onClose={() => setDetailsGiftCardId(null)}
        giftCardId={detailsGiftCardId}
        options={
          giftCardActionTarget
            ? getGiftCardOptions(giftCardActionTarget)
            : undefined
        }
      />

      {!!customer && !!isOpen && (
        <FullPageModal
          modalOpen={isEditCustomerModalOpen}
          setModalOpen={setIsEditCustomerModalOpen}
          name="Edit Customer">
          <CustomerForm
            setModalOpen={setIsEditCustomerModalOpen}
            selectedCustomer={customer}
            onCustomerSave={onCustomerSave}
          />
        </FullPageModal>
      )}

      {!!customer && !!editingGuest && !!isOpen && (
        <FullPageModal
          modalOpen={!!editingGuest}
          setModalOpen={() => setEditingGuest(null)}
          name={`Edit Guest: ${editingGuest.firstName} ${editingGuest.lastName}`.trim()}>
          <GuestForm
            setModalOpen={() => setEditingGuest(null)}
            customerId={customer.customerId}
            businessId={user?.business.id ?? ''}
            guest={editingGuest}
            onGuestSave={async () => {
              await refetch();
            }}
          />
        </FullPageModal>
      )}
    </>
  );
}

export default CustomerSideBarModal;
